import { ChangeEvent, FC, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { Typography, Button } from '@dreamteam-monorepo/ui';
import { useLeaguesStore } from '@/stores/LeaguesStore';
import { useAuthStore } from '@/stores/AuthStore';
import { BELeague } from '@/lib/leagueMappers';
import { getErrorMessage } from '@/lib/network';
import { LeagueSearchResultCard } from '@/components/LeagueSearchResultCard/LeagueSearchResultCard';
import { showGhLogin } from '@/lib/ghAuth';
import { trackFantasyEvent } from '@/analytics/fantasyEventTracking';

export interface JoinLeagueBannerProps {
  className?: string;
}

export const JoinLeagueBanner: FC<JoinLeagueBannerProps> = ({ className }) => {
  const navigate = useNavigate();
  const userId = useAuthStore(s => s.userId);
  const { getLeagueByCode, joinLeagueByCode, joining } = useLeaguesStore();

  const [pin, setPin] = useState('');
  const [searching, setSearching] = useState(false);
  const [foundLeague, setFoundLeague] = useState<BELeague | null>(null);
  const [notFound, setNotFound] = useState(false);

  const isActive = pin.trim().length > 0;

  const handleSearch = async () => {
    const code = pin.trim();
    if (!code) return;
    setSearching(true);
    setFoundLeague(null);
    setNotFound(false);
    try {
      const league = await getLeagueByCode(code);
      setFoundLeague(league);
    } catch {
      setNotFound(true);
    } finally {
      setSearching(false);
    }
  };

  const handleJoin = async () => {
    if (!foundLeague) return;
    if (!userId) {
      toast.error('You must be logged in to join a league');
      showGhLogin();
      return;
    }
    try {
      const leagueId = await joinLeagueByCode(foundLeague.join_code, userId);
      trackFantasyEvent('leagueJoinedByCode');
      toast.success('Successfully joined the league!');
      navigate(`/leagues/${leagueId}`);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not join the league. Please try again.'));
    }
  };

  return (
    <div
      className={[
        'bg-[--banner-bg] rounded-[--corner-md]',
        'px-[--padding-md] py-[--padding-lg]',
        'flex flex-col gap-[--padding-lg] w-full',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <Typography as="h2" variant="headline030" className="text-[--banner-text] !font-[900]">
        INVITED TO A LEAGUE?
      </Typography>

      <div className="flex flex-col gap-[--padding-xs]">
        <Typography variant="paragraph010" className="text-[--banner-text]">
          Enter League Pin
        </Typography>
        <div
          className={[
            'h-[48px] px-[--padding-md] rounded-[--corner-xs] border border-solid',
            'bg-[--banner-input-bg] flex items-center',
            isActive
              ? 'border-[--banner-input-border-active]'
              : 'border-[--banner-input-border-default]',
          ].join(' ')}
        >
          <input
            type="text"
            value={pin}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setPin(e.target.value);
              setFoundLeague(null);
              setNotFound(false);
            }}
            placeholder="eg. X123456X"
            className="flex-1 bg-transparent outline-none font-primary text-14 leading-20 text-[--banner-text] placeholder:text-[--banner-input-placeholder]"
          />
        </div>
      </div>

      <Button
        variant="contained"
        color="blue"
        disabled={!isActive || searching}
        loading={searching}
        onClick={handleSearch}
        className="w-full"
      >
        Find League
      </Button>

      {notFound && (
        <Typography variant="paragraph020" className="text-[--color-neutral-600]">
          No league found for that code.
        </Typography>
      )}

      {foundLeague && (
        <div className="flex flex-col gap-[--padding-md]">
          <Typography variant="subheadline020" className="text-[--banner-text]">
            1 league found
          </Typography>
          <LeagueSearchResultCard league={foundLeague} onJoin={handleJoin} joining={joining} />
        </div>
      )}
    </div>
  );
};
